import { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

const STEPS = [
  { target: "bottom-nav-planning", title: "Planning", text: "Qui travaille quel jour. Touchez un jour pour voir le détail." },
  { target: "bottom-nav-disponibilites", title: "Disponibilités", text: "Touchez les jours où vous êtes disponible : tout s'enregistre automatiquement." },
  { target: "bottom-nav-missions", title: "Missions", text: "Les remplacements et missions ponctuelles proposés à l'équipe. Répondez en un geste." },
  { target: "header-menu-btn", title: "Menu", text: "Profil, équipe, notifications, récapitulatif et Centre d'aide et de formation." },
];

function useRect(testid) {
  const [r, setR] = useState(null);
  useLayoutEffect(() => {
    if (!testid) { setR(null); return undefined; }
    const upd = () => { const el = document.querySelector(`[data-testid="${testid}"]`); setR(el ? el.getBoundingClientRect() : null); };
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, [testid]);
  return r;
}

function Card({ children, style, className = "" }) {
  return <div className={`fixed z-[102] w-[min(360px,calc(100vw-32px))] rounded-2xl bg-white p-5 shadow-2xl fade-up ${className}`} style={style} data-testid="tour-card">{children}</div>;
}

function Spot({ step, i, onNext, onSkip }) {
  const r = useRect(step.target);
  const below = r && r.top < window.innerHeight / 2;
  const left = r ? Math.min(Math.max(16, r.left + r.width / 2 - 180), window.innerWidth - Math.min(360, window.innerWidth - 32) - 16) : 16;
  return (
    <>
      {r && <div className="pointer-events-none fixed z-[101] rounded-2xl ring-4 ring-teal-400 transition-all duration-300" style={{ top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12, boxShadow: "0 0 0 9999px rgba(19,47,58,0.65)" }} />}
      {!r && <div className="fixed inset-0 z-[101] bg-[#132F3A]/65" />}
      <Card style={below ? { top: r.bottom + 16, left } : { bottom: r ? window.innerHeight - r.top + 16 : 100, left }}>
        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-teal-700">Étape {i + 1} / {STEPS.length}</div>
        <div className="text-lg font-bold">{step.title}</div>
        <p className="mt-1 text-slate-600">{step.text}</p>
        <div className="mt-4 flex justify-between gap-2">
          <button className="px-2 text-sm font-semibold text-slate-500" onClick={onSkip} data-testid="tour-skip-btn">Passer</button>
          <button className="btn-primary !min-h-[44px] !px-5" onClick={onNext} data-testid="tour-next-btn">{i === STEPS.length - 1 ? "Terminer" : "Suivant"}</button>
        </div>
      </Card>
    </>
  );
}

function Center({ children }) {
  return <div className="fixed inset-0 z-[101] flex items-center justify-center bg-[#132F3A]/65 p-4"><div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl fade-up" data-testid="tour-dialog">{children}</div></div>;
}

export function WelcomeTour() {
  const { user, current, reloadMe } = useApp();
  const nav = useNavigate();
  const [i, setI] = useState(-1);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (user?.tour_done === false && current) { setI(-1); setOpen(true); } }, [user?.tour_done, current]);
  if (!open) return null;
  const close = async (to) => { setOpen(false); await api.put("/auth/tour", { done: true }); await reloadMe(); if (to) nav(to); };
  if (i === -1) return (
    <Center>
      <img src="/brand/avelis-logo.png" alt="AVELIS" className="mx-auto w-[130px]" />
      <h2 className="mt-5 text-2xl font-bold">Bienvenue sur AVELIS</h2>
      <p className="mt-2 text-slate-600">Découvrez en 30 secondes où se trouvent les principales fonctionnalités.</p>
      <button className="btn-primary mt-6 w-full" onClick={() => setI(0)} data-testid="tour-start-btn"><Sparkles className="h-5 w-5" /> Découvrir la plateforme</button>
      <button className="mt-3 w-full py-2 text-sm font-semibold text-slate-500" onClick={() => close()} data-testid="tour-later-btn">Plus tard</button>
      <p className="mt-1 text-xs text-slate-400">Vous pourrez relancer la visite depuis le Centre d'aide.</p>
    </Center>
  );
  if (i >= STEPS.length) return (
    <Center>
      <GraduationCap className="mx-auto h-12 w-12 text-teal-600" />
      <h2 className="mt-4 text-xl font-bold">Vous êtes prête !</h2>
      <p className="mt-2 text-slate-600">Pour aller plus loin, le Centre d'aide et de formation vous guide pas à pas.</p>
      <button className="btn-primary mt-6 w-full" onClick={() => close("/aide")} data-testid="tour-open-help-btn">Ouvrir le centre de formation</button>
      <button className="mt-3 w-full py-2 text-sm font-semibold text-slate-500" onClick={() => close()} data-testid="tour-finish-btn">Terminer</button>
    </Center>
  );
  return <Spot step={STEPS[i]} i={i} onNext={() => setI(i + 1)} onSkip={() => close()} />;
}
